import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FormSection,
  Slider,
  Palette,
} from "@eweadn-qiankun/shared-components";
import { getSupportedDevicesSync } from "@eweadn-qiankun/shared-utils";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import {
  getMouseDpiLightingPreview,
  getMouseReceiverLightingPreview,
} from "../utils/mouseConfigPreviewUtils";
import { deviceHidesReceiverMonoAndPalette } from "../../../../../mouse-management/src/pages/lighting/dongleLightCapability";
import "../../../../../mouse-management/src/pages/lighting/index.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

function ReceiverLightingPreview({
  configData,
  solutionProvider,
  mouseDeviceId,
}: {
  configData?: Record<string, unknown>;
  solutionProvider?: string;
  mouseDeviceId?: string;
}) {
  const { t } = useTranslation();
  const isZhanli = solutionProvider === "zhanli";
  const hideReceiverMonoAndPalette =
    deviceHidesReceiverMonoAndPalette(mouseDeviceId);
  const values = useMemo(
    () => getMouseReceiverLightingPreview(configData, solutionProvider),
    [configData, solutionProvider]
  );

  // 与主页 ReceiverLighting 一致：展立 2=常亮 / 3=呼吸；匠盟相反
  const lightModes = isZhanli
    ? [
        { label: t("mouse.lighting.receiver.off"), value: 0 },
        { label: t("mouse.lighting.receiver.colorFlow"), value: 1 },
        ...(hideReceiverMonoAndPalette
          ? []
          : [
              { label: t("mouse.lighting.receiver.monoStatic"), value: 2 },
              { label: t("mouse.lighting.receiver.monoBreathing"), value: 3 },
            ]),
        { label: t("mouse.lighting.receiver.neon"), value: 4 },
        { label: t("mouse.lighting.receiver.mixedBreathing"), value: 5 },
        { label: t("mouse.lighting.receiver.rainbowStatic"), value: 6 },
      ]
    : [
        { label: t("mouse.lighting.receiver.off"), value: 0 },
        { label: t("mouse.lighting.receiver.colorFlow"), value: 1 },
        { label: t("mouse.lighting.receiver.monoBreathing"), value: 2 },
        { label: t("mouse.lighting.receiver.monoStatic"), value: 3 },
        { label: t("mouse.lighting.receiver.neon"), value: 4 },
        { label: t("mouse.lighting.receiver.mixedBreathing"), value: 5 },
        { label: t("mouse.lighting.receiver.rainbowStatic"), value: 6 },
      ];

  return (
    <div className="lighting-section">
      <h2 className="lighting-section-title">
        {t("mouse.lighting.receiver.title")}
      </h2>

      <FormSection title={t("mouse.lighting.receiver.mode")}>
        <div className="lighting-mode-buttons">
          {lightModes.map((lightMode) => (
            <button
              key={lightMode.value}
              type="button"
              className={`lighting-mode-btn ${values.mode === lightMode.value ? "active" : ""}`}
            >
              {lightMode.label}
            </button>
          ))}
        </div>
      </FormSection>

      <FormSection title={t("mouse.lighting.brightness")}>
        <div className="lighting-slider-wrap">
          <Slider
            min={10}
            max={100}
            step={10}
            value={values.brightness}
            onChange={() => undefined}
            disabled={values.brightnessDisabled}
            showValue
            unit="%"
          />
        </div>
      </FormSection>

      <FormSection title={t("mouse.lighting.speed")}>
        <div className="lighting-slider-wrap">
          <Slider
            min={10}
            max={100}
            step={10}
            value={values.speed}
            onChange={() => undefined}
            disabled={values.speedDisabled}
            showValue
            unit="%"
          />
        </div>
      </FormSection>

      {!hideReceiverMonoAndPalette && (
        <FormSection title={t("mouse.lighting.palette")}>
          <div
            className={`lighting-palette-inner community-mouse-lighting-palette${values.colorDisabled ? " lighting-palette-disabled" : ""}`}
          >
            <Palette value={values.colorHex} />
          </div>
        </FormSection>
      )}
    </div>
  );
}

function DpiLightingPreview({
  configData,
}: {
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const values = useMemo(
    () => getMouseDpiLightingPreview(configData),
    [configData]
  );

  const lightModes = [
    { label: t("mouse.lighting.dpi.off"), value: "off" },
    { label: t("mouse.lighting.dpi.static"), value: "static" },
    { label: t("mouse.lighting.dpi.breathing"), value: "breathing" },
  ] as const;

  return (
    <div className="lighting-section">
      <h2 className="lighting-section-title">
        {t("mouse.lighting.dpi.title")}
      </h2>

      <FormSection title={t("mouse.lighting.dpi.mode")}>
        <div className="lighting-mode-buttons">
          {lightModes.map((mode) => (
            <button
              key={mode.value}
              type="button"
              className={`lighting-mode-btn ${values.mode === mode.value ? "active" : ""}`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </FormSection>

      <FormSection title={t("mouse.lighting.brightness")}>
        <div className="lighting-slider-wrap">
          <Slider
            min={10}
            max={100}
            step={10}
            value={values.brightness}
            onChange={() => undefined}
            disabled={values.mode === "off" || values.mode === "breathing"}
            showValue
            unit="%"
          />
        </div>
      </FormSection>

      <FormSection title={t("mouse.lighting.speed")}>
        <div className="lighting-slider-wrap">
          <Slider
            min={20}
            max={100}
            step={20}
            value={values.speed}
            onChange={() => undefined}
            disabled={values.mode === "off" || values.mode === "static"}
            showValue
            unit="%"
          />
        </div>
      </FormSection>
    </div>
  );
}

function FreqLightingPreview({
  configData,
}: {
  configData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const perf = configData?.performance as Record<string, unknown> | undefined;
  const lighting = configData?.lighting as Record<string, unknown> | undefined;
  const freqLighting = lighting?.freq as Record<string, unknown> | undefined;
  const mode = Number(freqLighting?.mode ?? perf?.freqLightMode ?? 6);

  const lightModes = [
    { label: t("mouse.parameter.freqLightModeOff"), value: 6 },
    { label: t("mouse.parameter.freqLightModeWave"), value: 0 },
    { label: t("mouse.parameter.freqLightModeNeon"), value: 1 },
    { label: t("mouse.parameter.freqLightModePatrol"), value: 2 },
    { label: t("mouse.parameter.freqLightModeYoyo"), value: 3 },
    { label: t("mouse.parameter.freqLightModeOneway"), value: 4 },
    { label: t("mouse.parameter.freqLightModeBreath"), value: 5 },
  ];

  return (
    <div className="lighting-section">
      <h2 className="lighting-section-title">
        {t("mouse.parameter.freqLightSetting")}
      </h2>
      <FormSection description={t("mouse.parameter.freqLightSettingDesc")}>
        <div className="lighting-mode-buttons">
          {lightModes.map((lightMode) => (
            <button
              key={lightMode.value}
              type="button"
              className={`lighting-mode-btn ${mode === lightMode.value ? "active" : ""}`}
            >
              {lightMode.label}
            </button>
          ))}
        </div>
      </FormSection>
    </div>
  );
}

export default function MouseLightingSection({ meta, configData }: Props) {
  const isFreq = meta.solutionProvider === "freq";
  const showReceiverLighting = useMemo(() => {
    if (isFreq || meta.solutionProvider === "beiying") return false;
    const rows = (
      getSupportedDevicesSync().mouse as Array<{
        deviceId?: string;
        noDongleLight?: boolean;
      }>
    ).filter((d) => d.deviceId === meta.mouseDeviceId);
    // 与子应用 deviceSupportsDongleLight 一致：任一行允许接收器灯即展示
    return rows.length > 0 && rows.some((d) => d.noDongleLight !== true);
  }, [isFreq, meta.mouseDeviceId, meta.solutionProvider]);

  if (isFreq) {
    return (
      <div className="community-mouse-lighting-preview lighting-page">
        <div className="lighting-content">
          <div className="lighting-column">
            <FreqLightingPreview configData={configData} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="community-mouse-lighting-preview lighting-page">
      <div className="lighting-content">
        {showReceiverLighting ? (
          <>
            <div className="lighting-column">
              <ReceiverLightingPreview
                configData={configData}
                solutionProvider={meta.solutionProvider}
                mouseDeviceId={meta.mouseDeviceId}
              />
            </div>
            <div className="lighting-divider" />
          </>
        ) : null}

        <div className="lighting-column">
          <DpiLightingPreview configData={configData} />
        </div>
      </div>
    </div>
  );
}
